import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { dateParam, day } from "../../utils/constants/listFilters";
import {
  REVIEWED_BY_OPTIONS,
  REVIEW_FILTERS,
} from "../../utils/constants/AdIntelligence";
import DateRangeFilter from "../common/DateRangeFilter";
import FilterSelect from "../common/FilterSelect";
import useTaxonomyOptions from "../../hooks/useTaxonomyOptions";

/** Dates, category, the tab's own filter and, on reviewed tags, who reviewed them; kept in the URL. */
const ReviewFilterBar = ({ tab, filterCounts, onChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(
    () => searchParams.get("category") || ""
  );
  const [filter, setFilter] = useState(() => searchParams.get("filter") || "");
  const [reviewedBy, setReviewedBy] = useState(
    () => searchParams.get("reviewed_by") || ""
  );
  const [dates, setDates] = useState(() => ({
    from: dateParam(searchParams, "date_from"),
    to: dateParam(searchParams, "date_to"),
  }));
  const lastTab = useRef(tab);
  const { data: options } = useTaxonomyOptions();
  const tabFilters = REVIEW_FILTERS[tab] ?? [];
  const activeFilter = tabFilters.some((f) => f.value === filter) ? filter : "";
  const activeReviewedBy = tab === "reviewed" ? reviewedBy : "";
  const categoryOptions = (options?.categories ?? []).map((c) => ({
    value: c.key,
    label: c.enabled ? c.label : `${c.label} (off)`,
  }));
  const filterOptions = tabFilters.map((f) => ({
    value: f.value,
    label: `${f.label} · ${filterCounts?.[f.value] ?? 0}`,
  }));
  const alertFilter = tabFilters.find(
    (f) => f.alert && filterCounts?.[f.value] > 0 && f.value !== activeFilter
  );

  useEffect(() => {
    if (lastTab.current === tab) return;
    lastTab.current = tab;
    setFilter("");
  }, [tab]);

  useEffect(() => {
    const apiParams = {
      date_from: day(dates.from) || undefined,
      date_to: day(dates.to) || undefined,
      category: category || undefined,
      filter: activeFilter || undefined,
      reviewed_by: activeReviewedBy || undefined,
    };
    onChange({ apiParams, key: JSON.stringify(apiParams) });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const handleSetOrDelete = ({ key, value }) =>
          value ? next.set(key, value) : next.delete(key);
        handleSetOrDelete({ key: "date_from", value: day(dates.from) });
        handleSetOrDelete({ key: "date_to", value: day(dates.to) });
        handleSetOrDelete({ key: "category", value: category });
        handleSetOrDelete({ key: "filter", value: activeFilter });
        handleSetOrDelete({ key: "reviewed_by", value: activeReviewedBy });
        return next;
      },
      { replace: true }
    );
  }, [
    dates,
    category,
    activeFilter,
    activeReviewedBy,
    onChange,
    setSearchParams,
  ]);

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 p-4">
      <DateRangeFilter
        label={tab === "reviewed" ? "Reviewed date" : "Tagged date"}
        from={dates.from}
        to={dates.to}
        onChange={(from, to) => setDates({ from, to })}
      />
      <FilterSelect
        value={category}
        onChange={setCategory}
        options={categoryOptions}
        allLabel="All categories"
        placeholder="Category"
      />
      <FilterSelect
        value={activeFilter}
        onChange={setFilter}
        options={filterOptions}
        allLabel={tab === "reviewed" ? "Approved and corrected" : "All tags"}
        placeholder="Filter"
        width="w-[260px]"
      />
      {tab === "reviewed" && (
        <FilterSelect
          value={activeReviewedBy}
          onChange={setReviewedBy}
          options={REVIEWED_BY_OPTIONS}
          allLabel="Reviewed by anyone"
          placeholder="Reviewed by"
        />
      )}
      {alertFilter && (
        <button
          type="button"
          onClick={() => setFilter(alertFilter.value)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 text-sm font-medium text-rose-700 hover:bg-rose-100"
        >
          <AlertTriangle className="h-4 w-4" />
          {filterCounts[alertFilter.value]} {alertFilter.label.toLowerCase()}
        </button>
      )}
    </div>
  );
};

export default ReviewFilterBar;
